import { useState } from 'react'
import { CircularProgress } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import axios from 'axios'
import { checkErrorAPICall } from '../../../helpers/checkErrorAPICall'
import { useDispatch } from 'react-redux'

const BOOK_API = '/applet/v1/book'

interface EditableCellProps {
  value: string
  BOLId: string
  canEdit: boolean
  fontSize: number
  onSaveSuccess?: (newValue: string) => void
}

const EditableCell = ({ value, BOLId, canEdit, fontSize, onSaveSuccess }: EditableCellProps) => {
  const dispatch = useDispatch()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [localValue, setLocalValue] = useState<string | null>(null)

  const displayValue = localValue !== null ? localValue : value

  const handleSave = () => {
    if (editValue === (displayValue || '')) {
      setIsEditing(false)
      setEditValue('')
      return
    }

    setSaving(true)
    axios.put(`${BOOK_API}/${BOLId}`, {
      PRONmbr: editValue,
    }).then(() => {
      setLocalValue(editValue)
      setSaving(false)
      setIsEditing(false)
      setEditValue('')
      onSaveSuccess?.(editValue)
    }).catch(error => {
      setSaving(false)
      checkErrorAPICall(error, dispatch, 'Update PRO')
    })
  }

  const handleStartEdit = (e: React.MouseEvent) => {
    if (canEdit) {
      e.stopPropagation()
      setIsEditing(true)
      setEditValue(displayValue || '')
    }
  }

  if (isEditing) {
    return (
      <div
        className='d-flex align-items-center'
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#f8f9fa',
          borderRadius: 6,
          padding: '4px 8px',
          border: '1px solid #009ef7'
        }}
      >
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave()
            } else if (e.key === 'Escape') {
              setIsEditing(false)
              setEditValue('')
            }
          }}
          onBlur={handleSave}
          autoFocus
          style={{
            width: '100%',
            padding: 0,
            fontSize: fontSize,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent'
          }}
          disabled={saving}
        />
        {saving && <CircularProgress size={14} style={{ marginLeft: 4 }} />}
      </div>
    )
  }

  return (
    <div
      className={`d-flex align-items-center justify-content-between ${canEdit ? 'pro-editable-cell' : ''}`}
      onClick={handleStartEdit}
      style={{
        cursor: canEdit ? 'pointer' : 'default',
        minHeight: 28,
        padding: '4px 8px',
        borderRadius: 6
      }}
    >
      <span>{displayValue || ''}</span>
      {canEdit && (
        <EditIcon className="pro-edit-icon" style={{ width: 16, height: 16, color: '#888', marginLeft: 8 }} />
      )}
      <style>{`
        .pro-editable-cell {
          transition: background-color 0.1s ease;
        }
        .pro-editable-cell:hover {
          background-color: rgba(0, 0, 0, 0.08);
        }
        .pro-editable-cell .pro-edit-icon {
          opacity: 0;
          transition: opacity 0.1s ease;
        }
        .pro-editable-cell:hover .pro-edit-icon {
          opacity: 1;
        }
      `}</style>
    </div>
  )
}

export default EditableCell
